import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActionBar,
  ActionBarActions,
  ActionBarMessage,
  Button,
  ButtonLink,
  Checkbox,
  Column,
  Columns,
  ErrorMessage,
  Header,
  PageHeader,
  Row,
  Section,
  Text,
  TextInput,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { reactular } from '@vendhq/reactular'
import { T, useT } from '@transifex/react'
import { NoPermissionsPage } from '@vend/react-utilities'
import { useQuickKeyData } from '../components/quick-keys/useQuickKeyData'
import {
  AddProductParams,
  getChildrenForNode,
  getFirstBlankPositionForPage,
  getFirstChildForNode,
  getNodeAttribute,
  getRootNodeForLayout,
  setNodeAttribute,
  updateNode,
} from '../components/quick-keys/quickKeyUtils'
import { useQkNodesResource, useQuickKeysManager } from '../ng-compat/hooks'
import { QuickKeysWorkspace } from '../components/quick-keys/QuickKeysWorkspace'
import {
  useCreateFolder,
  useCreatePage,
  useCreateQuickKey,
} from '../components/quick-keys/useCreateQuickKey'
import { useDeleteQuickKey } from '../components/quick-keys/useDeleteQuickKey'
import { quickKeyLayoutsStore } from '../factories/database/quick-key-layouts-store'
import { useIsOffline } from '../components/shared/useIsOffline'
import { OfflineModal } from '../components/shared/OfflineModal'
import { $stateParams } from '../ng-compat/imports'
import { ApiNode } from '../components/quick-keys/utils/qk-types'
import {
  buildQuickKeysLayout,
  isPlaceholderPage,
} from '../components/quick-keys/utils/qk-layout'

export const QuickKeysSettings: FunctionComponent = () => {
  const { isNewLayout, layoutId } = ($stateParams as unknown) as {
    isNewLayout?: boolean
    layoutId: string
  }
  const t = useT()
  const quickKeysManager = useQuickKeysManager()
  const { isOffline } = useIsOffline()
  const [layoutData, setLayoutData, handleError] = useQuickKeyData(
    layoutId || ''
  )
  const [nameIsValid, setNameIsValid] = useState(true)
  const [lockedQuickKeys, setLockedQuickKeys] = useState<string[]>([])
  const [name, setName] = useState(layoutData ? layoutData.name : '')
  const [closeFolders, setCloseFolders] = useState<boolean | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const qkNodesResource = useQkNodesResource()
  const createQuickKey = useCreateQuickKey()
  const createPage = useCreatePage()
  const createFolder = useCreateFolder()
  const deleteQuickKey = useDeleteQuickKey()
  const hasSetInitialName = useRef(false)

  useEffect(() => {
    if (!hasSetInitialName.current && layoutData) {
      setName(layoutData.name)
      hasSetInitialName.current = true
    }
  }, [layoutData, name])

  useEffect(() => {
    const notify = () => {
      ToastNotifications.negative(
        t('Sorry there was a problem creating this layout, please try again.')
      )
    }
    quickKeysManager.on('quickKeyCreateLayoutError', notify)
    return () => {
      quickKeysManager.off('quickKeyCreateLayoutError', notify)
    }
  }, [quickKeysManager, t])

  const layout = useMemo(() => {
    if (!layoutData) {
      return null
    }
    return buildQuickKeysLayout(layoutData, {
      padEmptyRows: true,
      lockedQuickKeys,
    })
  }, [layoutData, lockedQuickKeys])

  useEffect(() => {
    if (!layoutData || closeFolders !== null) {
      return
    }

    const rootNode = getRootNodeForLayout(layoutData)

    if (!rootNode) {
      setCloseFolders(true)
      return
    }

    setCloseFolders(getNodeAttribute(rootNode, 'close_folder') === '1')
  }, [layoutData, closeFolders])

  const save = useCallback(async () => {
    if (!layoutData) {
      return
    }

    if (name.length === 0) {
      ToastNotifications.negative(
        t('Fill in the required information to continue')
      )
      return
    }

    let newLayoutData = { ...layoutData, name }
    const namePromise = quickKeysManager.updateLayout(layoutData.id, {
      name,
    })

    let closeNode = getRootNodeForLayout(layoutData)
    if (!closeNode) {
      closeNode = await createPage(null, 0, layoutData)
      newLayoutData.nodes.push(closeNode)
    }
    const updatedCloseNode = setNodeAttribute(
      closeNode,
      'close_folder',
      closeFolders ? '1' : '0'
    )
    newLayoutData = updateNode(newLayoutData, closeNode.id, updatedCloseNode)
    const closePromise = qkNodesResource.updateQuickKey(
      layoutData.id,
      updatedCloseNode
    )

    try {
      await Promise.all([namePromise, closePromise])
    } catch {
      handleError()
    }

    setLayoutData(newLayoutData)
    quickKeyLayoutsStore.put(newLayoutData)

    setHasChanges(false)
  }, [
    closeFolders,
    createPage,
    handleError,
    layoutData,
    name,
    qkNodesResource,
    quickKeysManager,
    setLayoutData,
    t,
  ])

  const updateCloseFolders = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setCloseFolders(e.target.checked)
      setHasChanges(true)
    },
    [setCloseFolders]
  )

  const setNode = useCallback(
    async (node: ApiNode) => {
      if (!layoutData) {
        return
      }
      setLockedQuickKeys(prev => [...prev, node.id])
      const updatedLayout = updateNode(layoutData, node.id, node)
      setLayoutData(updatedLayout)
      try {
        await qkNodesResource.updateQuickKey(layoutData.id, node)
      } catch {
        handleError()
      }
      await quickKeyLayoutsStore.put(updatedLayout)

      setLockedQuickKeys(prev => prev.filter(id => id !== node.id))
    },
    [handleError, layoutData, qkNodesResource, setLayoutData]
  )

  const getAllChildren = useCallback(
    (node: ApiNode) => {
      if (!layoutData) {
        return []
      }
      let children = getChildrenForNode(layoutData, node.id)
      children.forEach(child => {
        children = children.concat(getAllChildren(child))
      })
      return children
    },
    [layoutData]
  )

  const deleteNode = useCallback(
    async (node: ApiNode) => {
      if (!layoutData) {
        return
      }
      setLockedQuickKeys(prev => [...prev, node.id])

      const childIds = getAllChildren(node).map(child => child.id)
      const deletedIds = await deleteQuickKey({ node, layoutData }).catch(
        () => {
          handleError()
          return []
        }
      )
      const idsToDelete = [...deletedIds, ...childIds]
      const updatedLayout = {
        ...layoutData,
        nodes: layoutData.nodes.filter(n => !idsToDelete.includes(n.id)),
      }
      setLockedQuickKeys(prev => prev.filter(id => id !== node.id))
      setLayoutData(updatedLayout)
      await quickKeyLayoutsStore.put(updatedLayout)
    },
    [deleteQuickKey, getAllChildren, handleError, layoutData, setLayoutData]
  )

  const validateLayoutName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNameIsValid(e.target.value.length > 0)
  }, [])

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value
      setName(newName)
      validateLayoutName(e)
      setHasChanges(true)
    },
    [validateLayoutName]
  )

  const addProduct = useCallback(
    async ({ product, node, isChild, currentRoot }: AddProductParams) => {
      if (!layoutData) {
        return
      }
      setLockedQuickKeys(prev => [...prev, node.id])
      try {
        const newNodes = await createQuickKey({
          product,
          node,
          layoutData,
          isChild,
          currentRoot,
        })
        const newData = {
          ...layoutData,
          nodes: [...layoutData.nodes, ...newNodes],
        }
        setLayoutData(newData)
      } catch {
        handleError()
      }
      setLockedQuickKeys(prev => prev.filter(id => id !== node.id))
    },
    [createQuickKey, handleError, layoutData, setLayoutData]
  )

  const addNodeToFolder = useCallback(
    async (folder: ApiNode, node: ApiNode) => {
      if (!layoutData) {
        return
      }
      setLockedQuickKeys(prev => [...prev, folder.id, node.id])
      try {
        let page = getFirstChildForNode(layoutData, folder)

        if (!page || isPlaceholderPage(page)) {
          page = await createPage(folder.id, 0, layoutData)
        }
        let firstBlankPosition = getFirstBlankPositionForPage(layoutData, page)
        if (firstBlankPosition === null) {
          // Page is full create a new one
          page = await createPage(folder.id, 0, layoutData)
          // We know this page is empty so just assign position 0
          firstBlankPosition = 0
        }
        const updatedNode = {
          ...node,
          position: firstBlankPosition,
          parent_node_id: page.id,
        }
        const updatedNodes = layoutData.nodes.map(n => {
          if (n.id === node.id) {
            return updatedNode
          }
          if (n.id === page.id) {
            return page
          }
          return n
        })
        const newData = {
          ...layoutData,
          nodes: updatedNodes,
        }
        await qkNodesResource.updateQuickKey(layoutData.id, updatedNode)
        setLayoutData(newData)
        await quickKeyLayoutsStore.put(newData)
      } catch {
        handleError()
      }
      setLockedQuickKeys(prev =>
        prev.filter(id => id !== folder.id && id !== node.id)
      )
    },
    [createPage, handleError, layoutData, qkNodesResource, setLayoutData]
  )

  const combineNodesToFolder = useCallback(
    async (targetNode: ApiNode, droppedNode: ApiNode) => {
      if (!layoutData) {
        return
      }
      setLockedQuickKeys(prev => [...prev, targetNode.id, droppedNode.id])

      try {
        const newNodes = [
          ...layoutData.nodes.filter(
            n => n.id !== targetNode.id && n.id !== droppedNode.id
          ),
        ]
        await qkNodesResource.deleteQuickKey(layoutData.id, targetNode.id)
        const folder = await createFolder(targetNode, {
          ...layoutData,
          nodes: newNodes,
        })
        newNodes.push(folder)
        const page = await createPage(
          folder.id,
          targetNode.position,
          layoutData
        )
        const newTarget = await qkNodesResource.createQuickKey(layoutData.id, {
          ...targetNode,
          parent_node_id: page.id,
          position: 0,
          // @ts-ignore
          id: undefined,
        })
        const newDropped = await qkNodesResource.updateQuickKey(layoutData.id, {
          ...droppedNode,
          parent_node_id: page.id,
          position: 1,
        })
        newNodes.push(page)
        newNodes.push(newTarget)
        newNodes.push(newDropped)

        const newLayoutData = { ...layoutData, nodes: newNodes }
        setLayoutData(newLayoutData)
        await quickKeyLayoutsStore.put(newLayoutData)
      } catch {
        handleError()
      }
      setLockedQuickKeys(prev =>
        prev.filter(id => id !== targetNode.id && id !== droppedNode.id)
      )
    },
    [
      createFolder,
      createPage,
      handleError,
      layoutData,
      qkNodesResource,
      setLayoutData,
    ]
  )

  if (!quickKeysManager.hasPermission()) {
    return (
      <NoPermissionsPage
        copy={t(
          'You don’t have permission to edit quick keys. Switch to a user with permission.',
          { _context: 'Permission error page' }
        )}
        promptToSwitchUser={true}
        backgroundColor="transparent"
      />
    )
  }

  if (!layoutData) {
    return null
  }

  return (
    <>
      <PageHeader>
        {isNewLayout ? <T _str="Add Quick Key Layout" /> : layoutData.name}
      </PageHeader>
      <ActionBar variant="page">
        <ActionBarMessage>
          <T _str="Rename, reposition and recolor keys, or organize your products into folders." />
        </ActionBarMessage>
        <ActionBarActions>
          {hasChanges ? (
            <Button
              onClick={save}
              variant="go"
              className="pro-quick-key-edit-save-button"
            >
              <T _str="Save" />
            </Button>
          ) : (
            <ButtonLink
              href="/webregister/settings"
              variant="go"
              className="pro-quick-key-edit-save-button"
            >
              <T _str="Done" />
            </ButtonLink>
          )}
        </ActionBarActions>
      </ActionBar>
      <Section>
        <Row>
          <Columns space={10} collapseBelow="tablet">
            <Column width="3/12">
              <Header level="2" type="sub-heading">
                <T
                  _str="General"
                  _context="Quick keys edit form section header"
                />
              </Header>
            </Column>
            <Column width="9/12">
              <div className="vd-field vd-field--short">
                <label className="vd-label" htmlFor="layoutName">
                  <T
                    _str="Layout Name"
                    _context="Quick keys edit form layout name"
                  />
                </label>
                <div className="vd-value">
                  <TextInput
                    value={name}
                    name="layoutName"
                    placeholder={t('Quick Key Layout name')}
                    spellCheck="false"
                    autoComplete="off"
                    maxLength={64}
                    onChange={handleNameChange}
                    onBlur={validateLayoutName}
                    hasError={!nameIsValid}
                    autoFocus
                    data-testid="quick-key-layout-name"
                  />
                  {nameIsValid ? null : (
                    <ErrorMessage>
                      <T _str="This field is required" />
                    </ErrorMessage>
                  )}
                </div>
              </div>
              <div className="vd-field vd-field--short">
                <div className="vd-label">
                  <T
                    _str="Quick Key Behavior"
                    _context="Behaviour of closing quick key folders"
                  />
                </div>
                <div className="vd-value">
                  <Checkbox
                    checked={closeFolders || false}
                    onChange={updateCloseFolders}
                    label={t(
                      'Leave selected folder open until end of the sale'
                    )}
                    data-testid="close-folders-checkbox"
                  />
                </div>
              </div>
            </Column>
          </Columns>
        </Row>
        <hr className="vd-hr" />
        <Header level="2" type="sub-heading" className="vd-mb5">
          <T _str="Add Products" />
        </Header>
        <Row>
          <Columns space={10} collapseBelow="tablet">
            <Column width="3/12">
              <Text>
                <T _str="Search for products to add to your Quick Key layout. Drag and drop to rearrange." />
              </Text>
            </Column>
            <Column width="9/12">
              {layout ? (
                <QuickKeysWorkspace
                  layout={layout}
                  addProduct={addProduct}
                  setNode={setNode}
                  addNodeToFolder={addNodeToFolder}
                  combineNodesToFolder={combineNodesToFolder}
                  deleteNode={deleteNode}
                />
              ) : null}
            </Column>
          </Columns>
        </Row>
      </Section>
      {isOffline ? <OfflineModal /> : null}
    </>
  )
}

export const QuickKeysSettingsComponent = reactular(
  QuickKeysSettings,
  [],
  'reactWrapper'
)
